import {
  addMonths,
  differenceInCalendarDays,
  eachDayOfInterval,
  endOfDay,
  endOfMonth,
  format,
  formatDistance,
  getDaysInMonth,
  isAfter,
  startOfDay,
  startOfMonth,
  subDays,
  subMonths,
  subYears,
} from 'date-fns'

import { getPreviousPeriodDateRange } from '@buffer-mono/date-utils'

import { env } from '~publish/env'

export function isChannelSupportedByInsights(
  service: string | null | undefined,
  type: string | null | undefined,
): boolean {
  if (!service) return false
  if (service === 'startPage') return false
  if (service === 'googlebusiness') return false
  if (service === 'facebook' && type === 'group') return false
  if (service === 'instagram' && type === 'profile') return false
  return true
}

/**
 * Which content the all-channels view owes the actor: the Insights sections, or
 * one of the two states that stand in when no channel can be requested.
 *
 * `notSupported` has to win over `accessDenied`. An org whose channels are all
 * types Insights cannot report on — only Google Business Profiles, for example
 * — has nothing viewable either way, but a channel type Insights doesn't cover
 * is not a permission an admin could grant, so the access-denied copy would
 * send the actor to ask for access that doesn't exist. The per-channel view
 * orders the same two states the same way. @see CORE-7451
 *
 * No channels at all stays on `insights`: the router sends an org with none to
 * the connect-a-channel empty state before this page renders.
 */
export function getAllChannelsInsightsContentState({
  channels,
  requestChannelCount,
}: {
  channels: ReadonlyArray<{ service: string; type: string }>
  requestChannelCount: number
}): 'insights' | 'notSupported' | 'accessDenied' {
  if (requestChannelCount > 0 || channels.length === 0) return 'insights'

  const noChannelIsSupported = channels.every(
    (channel) => !isChannelSupportedByInsights(channel.service, channel.type),
  )
  return noChannelIsSupported ? 'notSupported' : 'accessDenied'
}

/**
 * Networks that have a follower-growth benchmark definition. A network outside
 * this list returns an empty series, so the allowlist only avoids the query:
 * `fetchChannelBenchmarks` runs authorization before it can answer "nothing
 * here", which is not free on a page every channel loads.
 *
 * @see domains/core-modules/src/domains/insights/benchmarks/constants.ts for the
 * definitions this mirrors.
 */
const BENCHMARKED_SERVICES = new Set([
  'linkedin',
  'instagram',
  'facebook',
  'twitter',
  'bluesky',
  'mastodon',
])

export function supportsBenchmarks(
  service: string | null | undefined,
): boolean {
  return service != null && BENCHMARKED_SERVICES.has(service)
}

/**
 * "Snapshot" networks that report followers only at the channel level — no
 * reach or views (Bluesky, Twitter/X, Mastodon). For these we skip fetching the
 * reach/views time series and hide the Visibility narrative (which plots views).
 */
export function isFollowersOnlyService(
  service: string | null | undefined,
): boolean {
  return (
    service === 'bluesky' || service === 'twitter' || service === 'mastodon'
  )
}

/**
 * Networks that report no channel-level metrics at all, not even followers —
 * Substack's API reads per-Note metrics only. The channel never records a
 * channel-level sync, so it must not wait on one, and every narrative view that
 * plots followers stays empty.
 */
export function isPostsOnlyService(
  service: string | null | undefined,
): boolean {
  return service === 'substack'
}

/**
 * Meta does not return Facebook Page insights (reach/views) until a Page reaches
 * this many followers, so a smaller Page legitimately has no channel-level data
 * rather than a transient gap. @see CORE-6425
 */
export const FACEBOOK_INSIGHTS_FOLLOWER_THRESHOLD = 100

/**
 * Whether a Facebook Page is below the follower count at which Meta starts
 * returning Page insights — the cue to show a tailored empty state explaining
 * the threshold instead of a generic "no data in range". The only follower
 * signal on the client is the followers series, so a range with no follower
 * reading returns false and falls back to the generic state. @see CORE-6425
 */
export function isBelowFacebookFollowerThreshold(args: {
  service: string | null | undefined
  channelType: string | null | undefined
  followers: ReadonlyArray<{ value?: number | null }>
}): boolean {
  const { service, channelType, followers } = args
  if (service !== 'facebook' || channelType !== 'page') return false
  const current = latestValue(followers)
  return current != null && current < FACEBOOK_INSIGHTS_FOLLOWER_THRESHOLD
}

/**
 * Human-readable label for a channel type, used in "Insights aren't available
 * for {label}" copy — on the per-channel unsupported screen and in the channel
 * filter's disabled-row tooltip. Falls back to the raw service for anything not
 * called out explicitly.
 *
 * @see isChannelSupportedByInsights for which (service, type) pairs are unsupported.
 */
export function getInsightsChannelTypeLabel(
  service: string,
  type: string,
): string {
  if (service === 'instagram' && type === 'profile')
    return 'Instagram Personal Profiles'
  if (service === 'facebook' && type === 'group') return 'Facebook Groups'
  if (service === 'startPage') return 'Start Page'
  if (service === 'googlebusiness') return 'Google Business Profile'
  return service
}

/**
 * The distinct channel-type labels across a set of channels, in first-seen
 * order. The all-channels "not supported" state names the one type it found so
 * its heading reads like the per-channel screen, and falls back to generic copy
 * when the org mixes several unsupported types.
 *
 * @see getInsightsChannelTypeLabel for how one channel is labelled.
 */
export function getInsightsChannelTypeLabels(
  channels: ReadonlyArray<{ service: string; type: string }>,
): string[] {
  return Array.from(
    new Set(
      channels.map((channel) =>
        getInsightsChannelTypeLabel(channel.service, channel.type),
      ),
    ),
  )
}

/**
 * Help Center article listing which channel types Insights supports. Linked
 * from both "not supported" states, so it lives here rather than in either.
 */
export const SUPPORTED_CHANNELS_ARTICLE_URL =
  'https://support.buffer.com/articles/supported-channels-LM3P7Y4zsp'

/**
 * Formats a date range into a human-readable string.
 * Same month: "Jan 3 – 30, 2026"
 * Cross-month, same year: "Jan 3 – Feb 2, 2026"
 * Cross-year: "Dec 3, 2025 – Jan 2, 2026"
 */
export function formatDateRange(startDate: Date, endDate: Date): string {
  const sameYear = startDate.getFullYear() === endDate.getFullYear()
  const sameMonth = sameYear && startDate.getMonth() === endDate.getMonth()

  if (sameMonth) {
    return `${format(startDate, 'MMM d')} - ${format(endDate, 'd, yyyy')}`
  }

  if (sameYear) {
    return `${format(startDate, 'MMM d')} - ${format(endDate, 'MMM d, yyyy')}`
  }

  return `${format(startDate, 'MMM d, yyyy')} - ${format(
    endDate,
    'MMM d, yyyy',
  )}`
}

/**
 * Date-range label for the comparison picker summary. Behaves like
 * {@link formatDateRange} but drops the year when the whole range sits in the
 * current year — "Mar 1 - 7" rather than "Mar 1 - 7, 2026" — so the summary
 * stays compact for the common same-year case. A range in a past (or future)
 * year keeps its year for disambiguation, e.g. a previous-year comparison.
 *
 * @param now - Reference date for "current year"; injectable for tests.
 */
export function formatComparisonDateRange(
  startDate: Date,
  endDate: Date,
  now: Date = new Date(),
): string {
  const currentYear = now.getFullYear()
  const bothInCurrentYear =
    startDate.getFullYear() === currentYear &&
    endDate.getFullYear() === currentYear

  if (!bothInCurrentYear) {
    return formatDateRange(startDate, endDate)
  }

  const sameMonth = startDate.getMonth() === endDate.getMonth()
  return sameMonth
    ? `${format(startDate, 'MMM d')} - ${format(endDate, 'd')}`
    : `${format(startDate, 'MMM d')} - ${format(endDate, 'MMM d')}`
}

/**
 * Subtitle string for an insights section that may include a comparison range.
 * - With comparison: `"Apr 26 - May 26, 2026 · Compared to Mar 26 - Apr 25, 2026"`
 * - Without comparison: `"Apr 26 - May 26, 2026"` (unchanged from {@link formatDateRange}).
 */
export function formatInsightsDateRange(
  startDate: Date,
  endDate: Date,
  comparisonStartDate?: Date | null,
  comparisonEndDate?: Date | null,
): string {
  const primary = formatDateRange(startDate, endDate)
  if (!comparisonStartDate || !comparisonEndDate) return primary
  return `${primary} · Compared to ${formatDateRange(
    comparisonStartDate,
    comparisonEndDate,
  )}`
}

export function getTimeAgo(
  timestamp: string | null | undefined,
  now: Date = new Date(),
): string {
  if (!timestamp) {
    return 'recently'
  }

  return formatDistance(new Date(timestamp), now, { addSuffix: true })
}

/**
 * Formats a Date object for RPC calls to buffer-analytics
 * Format: MM/DD/YYYY
 */
export function formatDateForRPCCall(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const year = date.getFullYear()
  return `${month}/${day}/${year}`
}

// Compact number formatter lives in its own dependency-free module so the
// insight logic can use it without importing this file's date-utils deps.
export { formatNumber } from './formatNumber'

export type DateRangeOption = {
  label: string
  value: string
}

export const DATE_RANGE_OPTIONS: DateRangeOption[] = [
  { label: '7 Days', value: 'last-7-days' },
  { label: '30 Days', value: 'last-30-days' },
  { label: 'Month to Date', value: 'month-to-date' },
  { label: 'Last Month', value: 'last-month' },
]

/**
 * Full-sentence label for a primary date-range preset — used in the comparison
 * picker summary (e.g. "Mar 29–Apr 5 (Last 7 days) vs. …"). This reads more
 * naturally than the terse {@link DATE_RANGE_OPTIONS} toggle labels ("7 Days").
 * Returns `null` for a custom or unrecognized range, where the caller falls
 * back to a computed day count instead.
 *
 * @see DATE_RANGE_OPTIONS for the preset values.
 */
export function primaryRangePresetLabel(value: string): string | null {
  switch (value) {
    case 'last-7-days':
      return 'Last 7 days'
    case 'last-30-days':
      return 'Last 30 days'
    case 'month-to-date':
      return 'Month to date'
    case 'last-month':
      return 'Last month'
    default:
      return null
  }
}

export type ComparisonMode =
  | 'previous-period'
  | 'previous-week'
  | 'previous-month'
  | 'previous-year'
  | 'custom'

export const DEFAULT_COMPARISON_MODE: ComparisonMode = 'previous-period'

export type ComparisonOption = {
  label: string
  value: ComparisonMode
  disabled?: boolean
}

export const COMPARISON_OPTIONS: ComparisonOption[] = [
  { label: 'Previous Period', value: 'previous-period' },
  { label: 'Previous Week', value: 'previous-week' },
  { label: 'Previous Month', value: 'previous-month' },
  { label: 'Previous Year', value: 'previous-year' },
  { label: 'Custom', value: 'custom' },
]

/** Short label for a comparison mode — used in tabs and chips. */
export function comparisonTabLabel(mode: ComparisonMode): string {
  switch (mode) {
    case 'previous-period':
      return 'Previous Period'
    case 'previous-week':
      return 'Previous Week'
    case 'previous-month':
      return 'Previous Month'
    case 'previous-year':
      return 'Previous Year'
    case 'custom':
      return 'Comparison'
  }
}

/** Caption-style comparison label (e.g. "vs prev. month") for share snapshots. */
export function comparisonShortLabel(mode: ComparisonMode): string {
  switch (mode) {
    case 'previous-period':
      return 'vs prev. period'
    case 'previous-week':
      return 'vs prev. week'
    case 'previous-month':
      return 'vs prev. month'
    case 'previous-year':
      return 'vs prev. year'
    case 'custom':
      return 'vs comparison'
  }
}

/**
 * Abbreviated comparison-mode label for the compact `Compare:` trigger button —
 * "Prev. Period" rather than the menu's full "Previous Period". Used for the
 * visible label only; the trigger's accessible name keeps the full word.
 *
 * @see comparisonTabLabel for the full-word label used elsewhere.
 */
export function comparisonTriggerLabel(mode: ComparisonMode): string {
  switch (mode) {
    case 'previous-period':
      return 'Prev. Period'
    case 'previous-week':
      return 'Prev. Week'
    case 'previous-month':
      return 'Prev. Month'
    case 'previous-year':
      return 'Prev. Year'
    case 'custom':
      return 'Custom'
  }
}

/**
 * Resolves the effective comparison window for a given mode and primary range.
 * Returns `null` only when `custom` is selected without valid dates — the
 * caller should fall back to the default mode in that case.
 */
export function getComparisonRange(
  mode: ComparisonMode,
  primaryStart: Date,
  primaryEnd: Date,
  customStart?: Date,
  customEnd?: Date,
): { startDate: Date; endDate: Date } | null {
  if (mode === 'previous-period') {
    const { previousStartDate, previousEndDate } = getPreviousPeriodDateRange(
      primaryStart,
      primaryEnd,
    )
    return { startDate: previousStartDate, endDate: previousEndDate }
  }
  if (mode === 'previous-week') {
    return {
      startDate: subDays(primaryStart, 7),
      endDate: subDays(primaryEnd, 7),
    }
  }
  if (mode === 'previous-month') {
    return {
      startDate: subMonths(primaryStart, 1),
      endDate: subMonths(primaryEnd, 1),
    }
  }
  if (mode === 'previous-year') {
    return {
      startDate: subYears(primaryStart, 1),
      endDate: subYears(primaryEnd, 1),
    }
  }
  if (customStart && customEnd && customStart <= customEnd) {
    return { startDate: customStart, endDate: customEnd }
  }
  return null
}

/**
 * True when the day-count of the comparison window differs from the primary.
 * Sum-based metrics carry the mismatch; rate-based metrics are unaffected.
 */
export function isComparisonLengthMismatched(
  primaryStart: Date,
  primaryEnd: Date,
  comparisonStart: Date,
  comparisonEnd: Date,
): boolean {
  const primaryDays = differenceInCalendarDays(primaryEnd, primaryStart)
  const comparisonDays = differenceInCalendarDays(
    comparisonEnd,
    comparisonStart,
  )
  return primaryDays !== comparisonDays
}

/** Inclusive day-counts to surface in a "Comparing X vs Y days" notice. */
export interface ComparisonLengthNotice {
  primaryDays: number
  comparisonDays: number
}

/**
 * Decides whether a "Comparing X vs Y days" notice should be shown when the
 * primary and comparison windows aren't directly comparable, returning the
 * inclusive day-counts to display (or `null` when they line up).
 *
 * Two cases trigger a notice:
 * 1. The windows simply span a different number of days.
 * 2. Month-to-Date: an in-progress month (e.g. 9 elapsed days) is compared
 *    against a full previous month. Here the windows can be equal-length, so
 *    we compare elapsed days against the comparison month's *total* days.
 */
export function getComparisonLengthNotice(
  primaryStart: Date,
  primaryEnd: Date,
  comparisonStart: Date | null | undefined,
  comparisonEnd: Date | null | undefined,
  selectedDateRange?: string,
): ComparisonLengthNotice | null {
  if (!comparisonStart || !comparisonEnd) return null

  const primaryDays = differenceInCalendarDays(primaryEnd, primaryStart) + 1
  const comparisonDays =
    differenceInCalendarDays(comparisonEnd, comparisonStart) + 1

  if (comparisonDays !== primaryDays) {
    return { primaryDays, comparisonDays }
  }

  if (selectedDateRange === 'month-to-date') {
    const comparisonMonthDays = getDaysInMonth(comparisonStart)
    if (comparisonMonthDays !== primaryDays) {
      return { primaryDays, comparisonDays: comparisonMonthDays }
    }
  }

  return null
}

/**
 * True when the primary period spans more than one calendar month, in which
 * case "Same Period Last Month" (`previous-month`) cannot produce an
 * equal-length comparison window and is disabled.
 */
export function isPeriodLongerThanOneMonth(start: Date, end: Date): boolean {
  return isAfter(end, addMonths(start, 1))
}

/**
 * True when the primary period spans more than a calendar week. "Previous
 * Week" (`previous-week`) only makes sense for week-sized or smaller
 * ranges, so the option is disabled above that threshold. A 7-day window
 * (e.g. Mon-Sun: `differenceInCalendarDays === 6`) is the maximum that
 * still qualifies as a "week".
 */
export function isPeriodLongerThanOneWeek(start: Date, end: Date): boolean {
  return differenceInCalendarDays(end, start) > 6
}

/**
 * Narrows a raw query-param value to a {@link ComparisonMode}, defaulting to
 * {@link DEFAULT_COMPARISON_MODE} for anything unrecognized.
 *
 * @see ComparisonMode for the accepted values.
 */
export function parseComparisonMode(raw: string | undefined): ComparisonMode {
  switch (raw) {
    case 'previous-period':
    case 'previous-week':
    case 'previous-month':
    case 'previous-year':
    case 'custom':
      return raw
    default:
      return DEFAULT_COMPARISON_MODE
  }
}

/**
 * Whether a comparison mode can produce a valid, equal-length window for the
 * given primary period — the single source of truth for "does this comparison
 * still fit?", shared by {@link useComparisonSelector}'s fallback and the
 * date-range reset notice.
 *
 *  - `previous-period` / `previous-year`: always fit — the window is derived
 *    from the primary range, so its length always matches.
 *  - `previous-month` / `previous-week`: only when the primary period is at
 *    most one month / one week (see {@link isPeriodLongerThanOneMonth} /
 *    {@link isPeriodLongerThanOneWeek}).
 *  - `custom`: only when both dates are present, the window length matches the
 *    primary period, it doesn't overlap the primary range, and it doesn't
 *    extend past `now`.
 *
 * @param now - Reference for the "not in the future" check; injectable for tests.
 */
export function canFulfilComparison(
  mode: ComparisonMode,
  primaryStart: Date,
  primaryEnd: Date,
  customStart?: Date,
  customEnd?: Date,
  now: Date = new Date(),
): boolean {
  switch (mode) {
    case 'previous-period':
    case 'previous-year':
      return true
    case 'previous-month':
      return !isPeriodLongerThanOneMonth(primaryStart, primaryEnd)
    case 'previous-week':
      return !isPeriodLongerThanOneWeek(primaryStart, primaryEnd)
    case 'custom': {
      if (!customStart || !customEnd) return false
      const periodLength = differenceInCalendarDays(primaryEnd, primaryStart)
      const customLength = differenceInCalendarDays(customEnd, customStart)
      const overlapsPrimary =
        customEnd >= primaryStart && customStart <= primaryEnd
      const extendsToFuture = customEnd > now
      return (
        customLength === periodLength && !overlapsPrimary && !extendsToFuture
      )
    }
  }
}

const DATE_RANGE_NATURAL_LANGUAGE: Record<string, string> = {
  'last-7-days': 'in the last 7 days',
  'last-30-days': 'in the last 30 days',
  'month-to-date': 'so far this month',
  'last-month': 'last month',
}

export function getNoPostsDescription(
  selectedDateRange: string | undefined,
  hasChannelFilter?: boolean,
): string {
  const period =
    selectedDateRange && DATE_RANGE_NATURAL_LANGUAGE[selectedDateRange]
  const channels = hasChannelFilter ? ' for these channels' : ''
  return `No posts found${channels} ${period ?? 'in the selected period'}.`
}

export function getNoDataDescription(
  selectedDateRange: string | undefined,
  hasChannelFilter?: boolean,
): string {
  const period =
    selectedDateRange && DATE_RANGE_NATURAL_LANGUAGE[selectedDateRange]
  const channels = hasChannelFilter ? ' for these channels' : ''
  return `No data found${channels} ${period ?? 'for the selected period'}.`
}

/**
 * Joins names into a readable sentence fragment: one ("A"), two ("A and B"),
 * or more ("A, B and C"). Used to list service names in copy.
 */
export function formatServiceList(names: string[]): string {
  if (names.length <= 1) return names[0] ?? ''
  if (names.length === 2) return `${names[0]} and ${names[1]}`
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}

export function calculateDateRange(
  value: string,
  now = new Date(),
): {
  startDate: Date
  endDate: Date
} {
  switch (value) {
    case 'last-7-days':
      // 7 inclusive days: today plus the 6 days before it.
      return { startDate: subDays(now, 6), endDate: now }
    case 'month-to-date':
      return { startDate: startOfMonth(now), endDate: now }
    case 'last-month':
      return {
        startDate: startOfMonth(subMonths(now, 1)),
        endDate: endOfMonth(subMonths(now, 1)),
      }
    case 'last-30-days':
    default:
      // 30 inclusive days: today plus the 29 days before it.
      return { startDate: subDays(now, 29), endDate: now }
  }
}

/**
 * Extracts the LinkedIn post URN from an external link and returns a Campaign Manager boost deep link URL.
 *
 * `origin=partner` and `appId` are required by LinkedIn so the redirect is
 * attributed back to Buffer's developer app for revenue tracking.
 *
 * Handles LinkedIn URL patterns like:
 *   https://www.linkedin.com/feed/update/urn:li:share:7284529399049121793/
 *   https://www.linkedin.com/feed/update/urn:li:activity:7284529399049121793/
 */
export function getLinkedInBoostUrl(
  externalLink: string | null | undefined,
  serviceId: string | null | undefined,
): string | null {
  if (!externalLink || !serviceId) return null
  const match = externalLink.match(/urn:li:[a-zA-Z]+:\d+/)
  if (!match) return null
  const params = new URLSearchParams({
    content: match[0],
    organization: serviceId,
    origin: 'partner',
    appId: env.VITE_LINKEDIN_APP_ID,
  })
  return `https://www.linkedin.com/campaignmanager/boost/campaigns/new/details?${params.toString()}`
}

/** Placeholder caption shown for a Top Posts row that has media but no text. */
export const MEDIA_ONLY_CAPTION = 'Media only'

/**
 * True when a Top Posts row has attachments but no text. These image/video-only
 * posts would otherwise render as a blank caption (CORE-6043); callers show
 * {@link MEDIA_ONLY_CAPTION} instead.
 */
export function isMediaOnlyPost(post: {
  text?: string | null
  assets?: ReadonlyArray<unknown> | null
}): boolean {
  return !post.text?.trim() && (post.assets?.length ?? 0) > 0
}

/**
 * Returns a short excerpt of the post text suitable for use in takeaway descriptions.
 */
export function getPostTopic(text: string | null | undefined): string {
  if (!text?.trim()) return 'your post'
  const words = text.trim().split(/\s+/)
  const short = words.slice(0, 6).join(' ')
  return words.length > 6 ? `${short}...` : short
}

export function zeroFillTimeSeries(
  data: Array<{ date: string; value?: number | null }>,
  startDate: Date,
  endDate: Date,
): Array<{ date: string; value: number }> {
  const existingByDate = new Map(
    data.map((d) => [d.date.slice(0, 10), d.value ?? 0]),
  )

  return eachDayOfInterval({
    start: startOfDay(startDate),
    end: endOfDay(endDate),
  }).map((day) => {
    const key = format(day, 'yyyy-MM-dd')
    return { date: key, value: existingByDate.get(key) ?? 0 }
  })
}

// Pull a channel's range start/end out of its daily series, tolerating the
// leading/trailing null gaps that real follower data has.
type ValuePoint = { value?: number | null }

/** The first non-null reading in a daily series (a channel's value at range start). */
export function firstValue(points: ReadonlyArray<ValuePoint>): number | null {
  for (const point of points) {
    if (point.value != null) return point.value
  }
  return null
}

/** The most recent non-null reading in a daily series (a channel's current value). */
export function latestValue(points: ReadonlyArray<ValuePoint>): number | null {
  for (let i = points.length - 1; i >= 0; i--) {
    const value = points[i].value
    if (value != null) return value
  }
  return null
}
